<template>
  <div class="radio-group" :class="flex_direction_class">
    <div v-for="(option, index) in opts" :key="index" class="radio-group-option">
      <input :id="`${group_name}-${index}`"
             :name="group_name"
             :value="option.value"
             :checked="option.checked"
             type="radio"
             @input="() => selectOption(index)">
      <label :for="`${group_name}-${index}`"
             class="btn"
             :class="`${option.checked ? checkedVariant : uncheckedVariant}`">
        <i v-if="option.checked" class="la la-check-circle" />{{ option.display }}
      </label>
    </div>
  </div>
</template>

<script>

let NAME_INDEX = 0;

export default {
  model: {
    prop: `selected`,
    event: `input`,
  },
  props: {
    selected: {
      type: null,
      default: null,
    },
    // array of radio options, which are in the form: `Option = { display: string, value: any, checked?: boolean }`
    options: {
      type: Array,
      required: true,
    },
    // the optional name of the radio input group (https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/radio#defining_a_radio_group)
    // if not supplied, a default is generated (see `beforeMount`)
    groupName: {
      type: String,
      required: false,
      default: null,
    },
    flexDirection: {
      type: String,
      required: false,
      default: "row",
    },
    checkedVariant: {
      type: String,
      required: false,
      default: "radio-btn checked",
    },
    uncheckedVariant: {
      type: String,
      required: false,
      default: "radio-btn",
    },
  },
  data: () => ({
    group_name: null,
    opts: null,
  }),
  computed: {
    checked() {
      const checked = {
        ...this.opts.find(opt => opt.checked),
      };
      delete checked.checked; // useless info
      return checked;
    },
    flex_direction_class() {
      // class name = "flex-<dir>" if dir is valid, otherwise default to 'flex-row'
      const class_name = [
        "row",
        "row-reverse",
        "column",
        "column-reverse",
      ].includes(this.flexDirection) ? `flex-${this.flexDirection}` : `flex-row`;
      return class_name;
    },
  },
  beforeMount() {
    // assign a default group name using NAME_INDEX, if one is not provided
    this.group_name = this.groupName ?? `radio-group-${NAME_INDEX++}`;
    this.opts = JSON.parse(JSON.stringify(this.options));

    if (this.selected) { // if there is a v-model value, we should override everything else and select it if its a valid option
      const value_opt_index = this.opts.findIndex(opt => opt.value === this.selected.value);
      if (value_opt_index !== -1) {
        this.selectOption(value_opt_index, true);
      }
    } else { // otherwise, we emit a change event with the first option with a 'checked: true' property (or option 0), setting the v-model
      const opt_index = (() => {
        const idx = this.opts.findIndex(opt => opt.checked);
        return idx !== -1 ? idx : 0;
      })();
      this.selectOption(opt_index);
    }
  },
  methods: {
    /**
     * Emits the selection's _value_ as an 'input' event, for v-model bindings or @change hooks.
     * A 'change' event is also fired, which contains the full selected radiogroup option including the value and label.
     */
    selectOption(option_index, supress_event = false) {
      this.opts.map(opt => delete opt.checked);
      this.opts.splice(option_index, 1, {
        ...this.opts[option_index],
        checked: true,
      });
      if (!supress_event) {
        this.$emit("input", this.opts[option_index].value);
        this.$emit("change", this.opts[option_index]);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "sass:map";

@use "~AssetsSass/_variables" as *;
@use "~AssetsSass/buttons";

// radio group: a flex box containing compact button-like elements
.radio-group {
  display: flex;
  flex-wrap: wrap;

  .radio-btn {
    @extend %btn-ss-base;

    display: flex;
    flex-direction: flex-row;
    border: 1px solid map.get($ss-adds, 'light-grey');
    border-right-width: 0;
    border-radius: 0;
    width: 100%;
    height: 100%;
    margin: 0;

    &:hover {
      background-color: map.get($ss, 'primary');
      color: white !important;

      border: 1px solid map.get($ss-adds, 'light-grey');
      border-right-width: 0;
    }

    &.checked {
      border: 1px solid color.adjust(map.get($ss-adds, 'light-grey'), $lightness: -14%, $space: hsl);
      border-right-width: 0;
      font-weight: bold;

      &:hover {
        color: white !important;
      }

      i {
        font-size: 1.2em;
        margin-right: -0.1em;
      }
    }
  }

  // here we re-add border rounding for the first and last children
  .radio-group-option:first-child {
    .btn {
      border-top-left-radius: .25rem;
      border-bottom-left-radius: .25rem;
    }
  }

  .radio-group-option:last-child {
    .btn {
      border-top-right-radius: .25rem;
      border-bottom-right-radius: .25rem;
    }
    .radio-btn {
      border-right-width: 1px;
      &.checked {
        border-right-width: 1px;
      }
    }
  }

  // default styling for the radio 'tabs'
  // they have `btn` classes also applied; see the markup
  .radio-group-option {
    input { // hide the actual input
      display: none;
    }
  }
}
</style>
